// Shared row definitions for the organiser itinerary (admin edit view, email preview,
// and the client app's itinerary page). Kept framework-agnostic (no Vue/axios deps) so it
// can be copied as-is between the ggb_app and ggb_client_app repos.

// Default intro paragraph shown under "Dear <name>," when the event has no
// event.itinerary_intro override saved. Blank lines mark paragraph breaks.
export const DEFAULT_ITINERARY_INTRO = `We would like to take this opportunity to thank you for choosing us as your party organiser. Please find below the details that we have organised for your group to make it an enjoyable experience.

As your group leader, you will be the chief organiser. So all correspondence with the suppliers will have your name for all bookings. Please state your name at the accommodation front desk, activity centre. nightclub doors, etc.

Your accommodation provider may ask for a security deposit upon arrival. This is a good behaviour bond between you and the supplier. Please see your accommodation details below for more info.

All the info you should need is on this itinerary.

*Please advise the accommodation or activity supplier if the accommodation or activity are not satisfactory to the relevant standards required.`

export function safeJSONParse(data) {
    if (Array.isArray(data) || (typeof data === 'object' && data !== null)) {
        return data
    }
    try {
        return data ? JSON.parse(data) : null
    } catch (e) {
        return null
    }
}

// Supplier/product descriptions are HTML (edited with the WYSIWYG). The itinerary's edit
// box is a plain textarea, so it shows them as text: paragraphs and list items become
// lines, tags are dropped and entities (&#39; &nbsp;) decoded.
export function htmlToText(html) {
    if (!html) return ''
    const marked = String(html)
        .replace(/\s+/g, ' ')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<li\b[^>]*>/gi, '\n• ')
        .replace(/<\/(p|div|h[1-6]|ul|ol)>/gi, '\n\n')
    const text = new DOMParser().parseFromString(marked, 'text/html').body.textContent || ''
    return text
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]*\n[ \t]*/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

// The reverse, for text typed into that edit box: blank lines start a new paragraph and
// single line breaks are kept, so the customer itinerary and email show it as typed.
export function textToHtml(text) {
    const escaped = String(text || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    return escaped.split(/\n{2,}/).map(p => p.trim()).filter(Boolean)
        .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
}

const looksLikeHtml = (value) => /<[a-z][^>]*>/i.test(value)

export function getSiteAddress(build) {
    return build.option?.site_address || build.supplier?.address || null
}

export function getSitePhone(build) {
    return build.option?.site_phone || build.supplier?.phone || null
}

// Name shown as the build's identity heading - prefers the specific product/room/activity
// name (matches how PreviewQuoteEmail names accommodation/activities) over the supplier's
// own name, falling back to the supplier name only when no product name is available.
export function getBuildName(build) {
    if (!build) return ''
    return build.option?.name || build.supplier?.products?.[0]?.name || build.supplier?.supplier_name || ''
}

function formatAccomGroupSize(build) {
    const groupsizes = safeJSONParse(build.accom_groupsize)
    if (!groupsizes || !groupsizes.length) return null
    return groupsizes.map((g, i) => `Day ${i + 1} - ${g}`).join(', ')
}

// Raw, unresolved row definitions for a build - i.e. before per-build overrides/hidden
// state (build.itinerary_row_settings) are applied. `formatDay` is an optional
// (date, format) => string callback so callers can plug in whichever date library they use.
function buildRowDefs(build, { formatDay } = {}) {
    const address = getSiteAddress(build) ?? build.supplier?.location?.name ?? null
    const phone = getSitePhone(build)

    // Note: the supplier/activity name and (for accommodation) the fixed "Self Catering
    // Accommodation" type line are rendered separately by callers, above these rows, since
    // they're identity fields rather than optional/overridable details. Venue is also
    // rendered separately by callers (it already has its own dedicated, always-editable
    // column) so it isn't duplicated here.
    if (build.type_id == 1) {
        return [
            { key: 'address', label: 'Address:', value: address },
            { key: 'room_type', label: 'Room Type:', value: build.option?.name || null },
            { key: 'phone', label: 'Contact Number:', value: phone },
            { key: 'deposit_info', label: 'Security Deposit:', value: build.supplier?.deposit_info || null },
            { key: 'breakfast_included', label: 'Breakfast Included:', value: build.supplier?.breakfast_included ? 'Yes' : 'No', alwaysShow: true },
            { key: 'has_bar', label: 'Bar Facilities:', value: build.supplier?.has_bar ? 'Yes' : 'No', alwaysShow: true },
            { key: 'group_size', label: 'Group Size:', value: formatAccomGroupSize(build) },
        ]
    }

    return [
        { key: 'activity_type', label: 'Activity Type:', value: build.option?.name || null },
        { key: 'day', label: 'Day:', value: formatDay ? formatDay(build.start_date, 'dddd') : build.start_date, alwaysShow: true },
        { key: 'group_size', label: 'Group Size:', value: build.groupsize || null },
        { key: 'time', label: 'Time:', value: build.start_time || null },
        { key: 'phone', label: 'Activity Contact:', value: phone },
        { key: 'address', label: 'Address:', value: address },
        { key: 'description', label: 'Description:', type: 'html', value: build.supplier?.description || build.option?.description || null },
    ]
}

// Every possible row for the build, merged with any saved overrides/hidden flags.
// Includes rows with no value so the editor can still show a toggle + input for them.
export function resolveItineraryRows(build, opts = {}) {
    const defs = buildRowDefs(build, opts)
    const settings = build.itinerary_row_settings || {}
    return defs.map(def => {
        const rowSetting = settings[def.key] || {}
        const overridden = rowSetting.value != null && rowSetting.value !== ''
        let value = overridden ? rowSetting.value : def.value
        // An html row's override is saved as typed (plain text); older ones may be HTML.
        if (overridden && def.type === 'html' && !looksLikeHtml(value)) {
            value = textToHtml(value)
        }
        const hasValue = value != null && value !== ''
        const hidden = typeof rowSetting.hidden === 'boolean' ? rowSetting.hidden : !(def.alwaysShow || hasValue)
        return {
            key: def.key,
            label: def.label,
            type: def.type || 'text',
            defaultValue: def.value,
            value,
            overridden,
            hidden,
        }
    })
}

// Only the rows that should actually be rendered to the customer (preview email, client itinerary).
export function visibleItineraryRows(build, opts = {}) {
    return resolveItineraryRows(build, opts).filter(row => !row.hidden && row.value != null && row.value !== '')
}
